// Per-agent mailbox endpoints (admin JWT). Shapes mirror the API exactly:
//   - MailAccountSummary → api/src/services/mail-account-store.ts
//   - the routes          → api/src/http/routes/mail-accounts.ts (list/create/quota/rotate/status/delete)
//
// A mailbox is `[peer]-[channel]@mail.cogent.tools` on the Stalwart box. The control
// plane stores metadata ONLY — the generated password is returned to the caller ONCE
// (on create/rotate) and never persisted.
//
// FEATURE-GATED (like integrations): the routes are mounted ONLY when the control plane
// has the mail box configured (MAIL_* env). Without it the route is un-mounted and Hono's
// notFound returns a BARE 404 (no JSON envelope) — a soft "mail not enabled" state the
// screen renders distinctly (see `isMailUnavailable`), separate from a real enveloped 404.
//
// Pattern: a thin data source over `apiFetch` with the success body runtime-validated at
// the boundary (vault Type_Assertion) so envelope drift fails loud as a ContractError.
import { z } from "zod";
import { apiFetch, ApiError } from "./client";

export type MailAccountStatus = "active" | "suspended";

/** Mailbox metadata (no secrets). */
export interface MailAccountSummary {
  id: string;
  orgId: string;
  channelId: string | null;
  channelName: string | null;
  peerId: string;
  address: string;
  stalwartAccountId: string;
  status: MailAccountStatus;
  quotaBytes: number;
  createdAt: string;
  rotatedAt: string | null;
}

/**
 * A mailbox enriched with live stats (list view). Each stat is null when the mail box is
 * unreachable (best-effort); an empty-but-reachable inbox is `messageCount: 0`,
 * `lastActivityAt: null`. Mirrors the API's MailAccountView.
 */
export interface MailAccount extends MailAccountSummary {
  usedBytes: number | null;
  messageCount: number | null;
  lastActivityAt: string | null;
}

const summaryShape = {
  id: z.string(),
  orgId: z.string(),
  channelId: z.string().nullable(),
  channelName: z.string().nullable(),
  peerId: z.string(),
  address: z.string(),
  stalwartAccountId: z.string(),
  status: z.enum(["active", "suspended"]),
  quotaBytes: z.number(),
  createdAt: z.string(),
  rotatedAt: z.string().nullable(),
};
const summarySchema = z.object(summaryShape) satisfies z.ZodType<MailAccountSummary>;
const accountSchema = z.object({
  ...summaryShape,
  usedBytes: z.number().nullable(),
  messageCount: z.number().nullable(),
  lastActivityAt: z.string().nullable(),
}) satisfies z.ZodType<MailAccount>;
const listResponseSchema = z.object({ mailboxes: z.array(accountSchema) });

/** Create/rotate return the one-time password alongside the summary. */
export interface CreatedMailbox extends MailAccountSummary {
  password: string;
}
const createdSchema = z.object({ ...summaryShape, password: z.string() }) satisfies z.ZodType<CreatedMailbox>;
const rotateSchema = z.object({ password: z.string() });

export interface CreateMailboxInput {
  peerId: string;
  /** Bind to one of the org's channels (its name seeds the address); omit for a peer-only box. */
  channelId?: string;
}

export async function listMailAccounts(signal?: AbortSignal): Promise<MailAccount[]> {
  const res = await apiFetch("/admin/mail-accounts", { auth: true, signal, schema: listResponseSchema });
  return res.mailboxes;
}

export function createMailAccount(body: CreateMailboxInput): Promise<CreatedMailbox> {
  return apiFetch("/admin/mail-accounts", { method: "POST", auth: true, body, schema: createdSchema });
}

export function setMailQuota(id: string, quotaBytes: number): Promise<MailAccountSummary> {
  return apiFetch(`/admin/mail-accounts/${encodeURIComponent(id)}/quota`, {
    method: "PATCH",
    auth: true,
    body: { quotaBytes },
    schema: summarySchema,
  });
}

export function rotateMailPassword(id: string): Promise<{ password: string }> {
  return apiFetch(`/admin/mail-accounts/${encodeURIComponent(id)}/rotate`, { method: "POST", auth: true, schema: rotateSchema });
}

export function setMailStatus(id: string, status: MailAccountStatus): Promise<MailAccountSummary> {
  return apiFetch(`/admin/mail-accounts/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    auth: true,
    body: { status },
    schema: summarySchema,
  });
}

export function deleteMailAccount(id: string): Promise<void> {
  return apiFetch(`/admin/mail-accounts/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

/**
 * True when the feature itself is unavailable because the control plane has no mail box
 * configured — the routes aren't mounted, so the app returns a bare 404 with no error
 * envelope (code `HTTP_404`). Distinct from a real enveloped 404 so the screen can show
 * "not enabled yet" rather than a scary failure.
 */
export function isMailUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === "HTTP_404";
}
